import React, { useState, useEffect, useRef, useCallback } from "react"; // Added useCallback
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import useWebOTP from '../../hooks/useWebOTP';
// --- CSS (Make sure relevant styles from previous steps are in login.css) ---
// Add the new styles provided in the next step to login.css

function VerifyOtp() {
    const navigate = useNavigate();
    const location = useLocation();
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; // Adjust fallback if needed
    const TWOFACTOR_API_KEY = process.env.REACT_APP_2FACTOR_API_KEY; // Get API Key from environment

    const otpInputRefs = useRef([]);
    const timerRef = useRef(null); // Ref for the timer interval
    // --- State ---
    const [phone, setPhone] = useState('');
    const [sessionId, setSessionId] = useState(''); // State to store the session ID from 2Factor
    const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
    const [otpLoading, setOtpLoading] = useState(false);
    const [isOtpFormValid, setIsOtpFormValid] = useState(false);
    const [remainingTime, setRemainingTime] = useState(60); // Timer state
    const [resendLoading, setResendLoading] = useState(false); // Resend loading state
    const [verifyLoading, setVerifyLoading] = useState(false); // Loading for verification

    // --- Timer Logic ---
    const startTimer = useCallback(() => {
        clearInterval(timerRef.current);
        setRemainingTime(60);
        timerRef.current = setInterval(() => {
            setRemainingTime(prevTime => {
                if (prevTime <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);
    }, []);
    // --- Effects ---
    // Load CSS
    useEffect(() => {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "/assets/css/login.css";
        document.head.appendChild(link);
        return () => {
            if (document.head.contains(link)) {
                document.head.removeChild(link);
            }
        };
    }, []);


    // Get phone number and session ID from navigation state, start timer
    useEffect(() => {
        let phoneFromState = '';
        let sessionIdFromState = '';

        if (location.state?.phone && location.state?.sessionId) {
            const rawPhoneFromState = location.state.phone; // Get the original phone number
            sessionIdFromState = location.state.sessionId;

            const formattedPhone = rawPhoneFromState.startsWith('+')
                ? rawPhoneFromState.substring(1) // Remove the first character (+)
                : rawPhoneFromState; // Use as is if no '+'

            setPhone(formattedPhone);
            setSessionId(sessionIdFromState);

            console.log("Received phone (formatted):", formattedPhone, "and sessionId:", sessionIdFromState); // Log the modified number
            startTimer(); // Start timer only if phone and session ID are present

        } else {
            console.error("Missing phone number or session ID in location state.");
            toast.error("Verification process error. Please register again.", { autoClose: 4000 });
            navigate('/registration'); // Redirect back if data is missing
        }

        return () => clearInterval(timerRef.current);
    }, [location.state, navigate, startTimer]); // Dependencies

    // Validate OTP form
    useEffect(() => {
        const isAllDigitsFilled = otpDigits.every(digit => digit.trim() !== '' && /^\d$/.test(digit));
        setIsOtpFormValid(isAllDigitsFilled);
    }, [otpDigits]);

    // WebOTP API SMS Auto-Detection for mobile browsers
    useWebOTP({
        enabled: Boolean(phone) && remainingTime > 0,
        onOtpReceived: (code) => {
            if (code && code.length === 6) {
                const digits = code.split('');
                setOtpDigits(digits);
                handleOtpSubmit(null, code);
            }
        },
    });

    const handleDigitChange = (index, value) => {
        const cleaned = String(value || '').replace(/\D/g, '');

        if (cleaned.length > 1) {
            const digits = cleaned.slice(0, 6).split('');
            const newOtpDigits = [...otpDigits];
            digits.forEach((d, i) => {
                if (index + i < 6) {
                    newOtpDigits[index + i] = d;
                }
            });
            setOtpDigits(newOtpDigits);
            const lastIndex = Math.min(5, index + digits.length - 1);
            if (otpInputRefs.current[lastIndex]) {
                otpInputRefs.current[lastIndex].focus();
            }
            if (newOtpDigits.every((digit) => digit.trim() !== '')) {
                handleOtpSubmit(null, newOtpDigits.join(''));
            }
            return;
        }

        const singleDigit = cleaned.slice(-1);
        const newOtpDigits = [...otpDigits];
        newOtpDigits[index] = singleDigit;
        setOtpDigits(newOtpDigits);

        if (singleDigit && index < 5) {
            otpInputRefs.current[index + 1]?.focus();
        }
        if (newOtpDigits.every(digit => digit.trim() !== '')) {
            handleOtpSubmit(null, newOtpDigits.join(''));
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData ? e.clipboardData.getData('text') : '';
        const cleaned = pastedData.replace(/\D/g, '').slice(0, 6);
        if (cleaned.length > 0) {
            const digits = cleaned.split('');
            const newOtpDigits = Array(6).fill('');
            digits.forEach((d, i) => { newOtpDigits[i] = d; });
            setOtpDigits(newOtpDigits);
            const lastIndex = Math.min(5, digits.length - 1);
            if (otpInputRefs.current[lastIndex]) {
                otpInputRefs.current[lastIndex].focus();
            }
            if (cleaned.length === 6) {
                handleOtpSubmit(null, cleaned);
            }
        }
    };

    const handleOtpKeyDown = (e, index) => {
        if (e.key === 'Backspace') {
            if (!otpDigits[index] && index > 0) {
                const newOtpDigits = [...otpDigits];
                newOtpDigits[index - 1] = '';
                setOtpDigits(newOtpDigits);
                otpInputRefs.current[index - 1]?.focus();
            }
        }
    };

    const handlePasteFromClipboard = async () => {
        try {
            if (navigator.clipboard && navigator.clipboard.readText) {
                const text = await navigator.clipboard.readText();
                const cleaned = String(text || '').replace(/\D/g, '').slice(0, 6);
                if (cleaned.length > 0) {
                    const digits = cleaned.split('');
                    const newOtpDigits = Array(6).fill('');
                    digits.forEach((d, i) => { newOtpDigits[i] = d; });
                    setOtpDigits(newOtpDigits);
                    const lastIndex = Math.min(5, digits.length - 1);
                    if (otpInputRefs.current && otpInputRefs.current[lastIndex]) {
                        otpInputRefs.current[lastIndex].focus();
                    }
                    if (cleaned.length === 6) {
                        handleOtpSubmit(null, cleaned);
                    }
                }
            }
        } catch (err) {
            console.warn("Clipboard access notice:", err);
        }
    };


    // --- Button Styling & State ---
    const isOtpButtonDisabled = !isOtpFormValid || verifyLoading || resendLoading; // Disable if any loading is true
    const baseButtonStyle = { /* ... (same styles as before) ... */
        color: 'white', padding: '10px 25px',
        borderRadius: '8px', border: '1px solid transparent',
        fontSize: '0.95rem', fontWeight: '500',
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '40px', minWidth: '100px'
    };

    const buttonStyle = {
        cursor: !isOtpButtonDisabled ? 'pointer' : 'not-allowed',
        backgroundColor: !isOtpButtonDisabled ? 'rgb(9, 156, 241)' : '#717171',
        color: 'white',
        padding: '10px 40px',
        borderRadius: '15px',
        border: `1px solid ${!isOtpButtonDisabled ? 'rgb(9, 156, 241)' : '#717171'}`,
        fontSize: '16px',
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
        display: 'flex', // Added for centering loader/text
        alignItems: 'center', // Added for centering loader/text
        justifyContent: 'center', // Added for centering loader/text
        minHeight: '45px' // Ensure button height is consistent with/without loader
    };

    const verifyButtonStyle = { /* ... (same styles as before) ... */
        ...baseButtonStyle,
        cursor: !isOtpButtonDisabled ? 'pointer' : 'not-allowed',
        backgroundColor: !isOtpButtonDisabled ? '#717171' : '#d3d3d3',
        border: `1px solid ${!isOtpButtonDisabled ? '#717171' : '#d3d3d3'}`,
    };


    const cancelButtonStyle = { /* ... (same styles as before) ... */
        ...baseButtonStyle,
        cursor: 'pointer', backgroundColor: 'white',
        color: '#717171', border: `1px solid #ccc`,
        marginLeft: '10px',
    };

    // --- Format Timer ---
    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = time % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    // --- Resend OTP Handler ---
    const handleResendOtp = async (e) => {
        e?.preventDefault(); // Prevent default link behavior if called from 'a' tag
        if (remainingTime > 0 || resendLoading || !phone || !TWOFACTOR_API_KEY) {
            if (!TWOFACTOR_API_KEY) console.error("2Factor API Key missing for resend.");
            return; // Prevent resend if timer running, loading, no phone, or no API key
        }

        setResendLoading(true);
        console.log("Resending OTP for phone", phone);
        const otpSendUrl = `https://2factor.in/API/V1/${TWOFACTOR_API_KEY}/SMS/${phone}/AUTOGEN/Mangoloops+OTP`; // Or just AUTOGEN

        try {
            const response = await axios.get(otpSendUrl);
            console.log("2Factor.in Resend OTP Response:", response.data);

            if (response.data?.Status === 'Success') {
                const newSessionId = response.data.Details;
                setSessionId(newSessionId);
                toast.success("New OTP sent successfully!", { autoClose: 3000 });
                setOtpDigits(['', '', '', '', '', '']); // Clear OTP fields
                otpInputRefs.current[0]?.focus(); // Focus first input
                startTimer(); // Restart the timer
            } else {
                const errorMessage = response.data?.Details || "Failed to resend OTP.";
                toast.error(errorMessage, { autoClose: 4000 });
                console.error("2Factor.in Resend OTP Error:", errorMessage);
            }
        } catch (error) {
            console.error("Error resending OTP via 2Factor.in:", error);
            let errorMessage = "Failed to resend OTP due to a network or server issue.";
            if (error.response) {
                console.error("2Factor Resend Error Data:", error.response.data);
                errorMessage = `OTP service error (${error.response.status}). Please try again later.`;
            } else if (error.request) {
                errorMessage = "Could not reach OTP service for resend. Check connection.";
            }
            toast.error(errorMessage, { autoClose: 4000 });
        } finally {
            setResendLoading(false);
        }
    };

    // --- OTP Verification Form Submission ---
    const handleOtpSubmit = async (e, otpOverride = null) => {
        if (e) e.preventDefault();
        if (!phone || !sessionId || !TWOFACTOR_API_KEY) {
            if (!TWOFACTOR_API_KEY) console.error("2Factor API Key missing for verification.");
            if (!sessionId) console.error("Session ID missing for verification.");
            return;
        }

        setVerifyLoading(true);
        const enteredOtp = otpOverride || otpDigits.join('');
        console.log(`Verifying OTP ${enteredOtp} for session ${sessionId}`);
        const verifyUrl = `https://2factor.in/API/V1/${TWOFACTOR_API_KEY}/SMS/VERIFY/${sessionId}/${enteredOtp}`;

        try {
            const response = await axios.get(verifyUrl);
            console.log("2Factor.in Verify OTP Response:", response.data);

            if (response.data?.Status === 'Success') {
                // OTP Verified Successfully by 2Factor.in
                toast.success(response.data.Details || "OTP Verified Successfully!", { // Use message from 2Factor if available
                    position: "top-right", autoClose: 3000,
                    onClose: () => {
                        console.log("OTP verified via 2Factor. Navigating to login.");
                        navigate('/admin/dashboard'); // Navigate to login page on success
                    }
                });
                clearInterval(timerRef.current); // Stop timer on success

            } else {
                // OTP Verification Failed (Mismatch, Expired, etc.)
                const errorMessage = response.data?.Details || "Invalid OTP or verification failed.";
                toast.error(errorMessage, { position: "top-right", autoClose: 5000 });

            }
        } catch (error) {
            console.error("Error verifying OTP via 2Factor.in:", error);
            let errorMessage = "Failed to verify OTP due to a network or server issue.";
            if (error.response) {
                console.error("2Factor Verify Error Data:", error.response.data);
                errorMessage = `OTP verification service error (${error.response.status}). Please try again.`;
            } else if (error.request) {
                errorMessage = "Could not reach OTP verification service. Check connection.";
            }
            toast.error(errorMessage, { position: "top-right", autoClose: 5000 });
        } finally {
            setVerifyLoading(false);
        }
    };

    // --- Cancel Handler ---
    const handleCancel = () => {
        navigate('/registration'); // Go back to registration
    };


    // --- JSX Structure ---
    return (
        <div className="login-page d-flex align-items-center justify-content-center min-vh-100">
            <ToastContainer position="top-right" autoClose={5000} /* ... other props ... */ />
            <div className="container-fluid">
                <div className="row justify-content-center align-items-center">

                    {/* Left Section (remains the same) */}
                    <div className="col-lg-6 d-none d-lg-flex flex-column align-items-center text-center left-section">
                        <div className="logo-sec"> <img src="/logo-mangoloops.png" alt="Mangoloops Logo" /> </div>
                        <h1 className="login-title">Book Your Dream Vacation in 48 Hours</h1>
                        <p className="login-pg-p">Streamline your travel planning with seamless booking options. One-stop solution for every traveler.</p>
                    </div>

                    <div className="col-lg-6 col-12 d-flex flex-column justify-content-center right-section">
                        <div className="otp-modal-content">
                            {phone ? (
                                <>
                                    <h2 className="otp-verification-title">OTP Verification</h2>
                                    <p className="otp-instruction">
                                        Please enter the OTP (One-Time Password) sent to
                                        your registered mobile number <strong className="service-type">({phone})</strong> to
                                        complete your verification.
                                    </p>
                                    <form onSubmit={handleOtpSubmit} noValidate>
                                        <div className="otp-input-container">
                                            {otpDigits.map((digit, index) => (
                                                <input
                                                    key={index}
                                                    ref={el => otpInputRefs.current[index] = el}
                                                    type="tel"
                                                    inputMode="numeric"
                                                    className="otp-digit-input"
                                                    maxLength="6"
                                                    autoComplete="one-time-code"
                                                    value={digit}
                                                    onChange={(e) => handleDigitChange(index, e.target.value)}
                                                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                                                    onPaste={handlePaste}
                                                    onFocus={(e) => e.target.select()}
                                                    disabled={otpLoading || resendLoading}
                                                    required
                                                />
                                            ))}
                                        </div>

                                        {/* Timer and Resend */}
                                        <div className="otp-timer-resend-container">
                                            <div className="otp-timer">
                                                Remaining time: {formatTime(remainingTime)}
                                            </div>
                                            <div className="otp-resend">
                                                Didn't get the code?{' '}
                                                {remainingTime > 0 || resendLoading ? (
                                                    <span className="otp-resend-disabled" title={`Resend available in ${remainingTime}s`}>
                                                        Resend {resendLoading ? '(Sending...)' : ''}
                                                    </span>
                                                ) : (
                                                    <a href="#" onClick={handleResendOtp} className="otp-resend-active">
                                                        Resend
                                                    </a>
                                                )}
                                            </div>
                                        </div>

                                        {/* Button Group */}
                                        <div className="otp-button-group">
                                            <button type="submit" className="otp-button verify-button"
                                                style={buttonStyle} disabled={isOtpButtonDisabled}>
                                                {otpLoading ? <div className="loader-spinner-small"></div> : 'Verify'}
                                            </button>
                                            <button type="button" className="otp-button cancel-button" style={cancelButtonStyle} onClick={handleCancel} disabled={otpLoading || resendLoading}>
                                                Cancel
                                            </button>
                                        </div>
                                    </form>

                                </>
                            ) : (
                                <p>Loading verification...</p> // Placeholder
                            )}
                        </div> {/* End otp-modal-content */}
                    </div> {/* End Right Section Col */}
                </div>
            </div>
        </div>
    );
}

export default VerifyOtp;